/**
 * Admin Access Grid — Track 5.1.
 *
 * Every active grant in one auditor-grade table: who, what role, what
 * scope, granted_by, granted_at, expires_at, last_used_at, state.
 * Inline filters by role, expires-soon, state. The auditor's year-end
 * query is one CSV export from this page.
 *
 * Admin only. Backend: /api/cdm/role-grants*.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Lock, RefreshCw, ChevronLeft, Plus, Trash2, Edit2, Download, X,
  CheckCircle, AlertTriangle, Clock,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Grant {
  id: string
  user_id: string
  user_email: string
  role: string
  scope_kind: string
  scope_filter: Record<string, any>
  granted_by: string
  granted_at: string | null
  expires_at: string | null
  days_until_expiry: number | null
  last_used_at: string | null
  last_attested_at: string | null
  reason: string
  state: string
}

interface Rollup {
  active: number
  suspended: number
  revoked: number
  global_view: number
  expiring_soon: number
}

interface PendingRequest {
  id: string
  user_email: string
  role: string
  scope_kind: string
  scope_filter: Record<string, any>
  reason: string
  state: string
  requested_at: string | null
}

const ROLES = ['viewer', 'analyst', 'operator', 'admin'] as const
const SCOPE_KINDS = ['home_org_cascade', 'explicit_grant', 'region_view', 'global_view'] as const
const STATES = ['active', 'suspended', 'revoked', 'all'] as const

interface FormState {
  user_email: string
  role: string
  scope_kind: string
  scope_filter_json: string
  expires_at: string
  reason: string
}

const FRESH_FORM: FormState = {
  user_email: '',
  role: 'viewer',
  scope_kind: 'home_org_cascade',
  scope_filter_json: '{}',
  expires_at: '',
  reason: '',
}

function expiryClass(days: number | null): string {
  if (days === null) return 'text-text-muted'
  if (days < 7)  return 'text-rose-light font-semibold'
  if (days < 30) return 'text-amber-light'
  return 'text-emerald-light'
}

function lastUsedClass(iso: string | null): string {
  if (!iso) return 'text-text-muted italic'
  const days = (Date.now() - new Date(iso).getTime()) / 86400000
  if (days > 90) return 'text-amber-light'
  return 'text-text-secondary'
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toISOString().slice(0, 10)
  } catch {
    return iso
  }
}

function fmtRel(iso: string | null): string {
  if (!iso) return 'never'
  try {
    const days = Math.round((Date.now() - new Date(iso).getTime()) / 86400000)
    if (days === 0) return 'today'
    if (days === 1) return '1d ago'
    if (days < 30)  return `${days}d ago`
    if (days < 365) return `${Math.round(days/30)}mo ago`
    return `${Math.round(days/365)}y ago`
  } catch {
    return iso
  }
}

export default function AdminAccessPage() {
  const [grants, setGrants] = useState<Grant[]>([])
  const [rollup, setRollup] = useState<Rollup | null>(null)
  const [pending, setPending] = useState<PendingRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const [stateFilter, setStateFilter] = useState<string>('active')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [scopeFilter, setScopeFilter] = useState<string>('')
  const [expiringSoon, setExpiringSoon] = useState(false)
  const [searchEmail, setSearchEmail] = useState('')

  const [editing, setEditing] = useState<Grant | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(FRESH_FORM)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const load = () => {
    setLoading(true)
    const params: Record<string, any> = { state: stateFilter, limit: 1000 }
    if (roleFilter)  params.role = roleFilter
    if (scopeFilter) params.scope_kind = scopeFilter
    if (expiringSoon) params.expires_within_days = 14
    if (searchEmail) params.user_email = searchEmail

    api.get('/api/cdm/role-grants', { params })
      .then(r => {
        setGrants(r.data?.grants || [])
        setRollup(r.data?.rollup || null)
      })
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))

    api.get('/api/cdm/access-requests', { params: { state: 'pending' } })
      .then(r => setPending(r.data?.requests || []))
      .catch(() => {})
  }

  useEffect(() => { load() // eslint-disable-next-line
  }, [stateFilter, roleFilter, scopeFilter, expiringSoon])

  const startCreate = () => {
    setForm(FRESH_FORM)
    setEditing(null)
    setCreating(true)
    setMessage('')
  }

  const startEdit = (g: Grant) => {
    setForm({
      user_email: g.user_email,
      role: g.role,
      scope_kind: g.scope_kind,
      scope_filter_json: JSON.stringify(g.scope_filter || {}, null, 2),
      expires_at: g.expires_at ? g.expires_at.slice(0, 16) : '',
      reason: g.reason || '',
    })
    setEditing(g)
    setCreating(false)
    setMessage('')
  }

  const closeForm = () => {
    setEditing(null)
    setCreating(false)
  }

  const submitForm = async () => {
    let scopeFilterObj: any = {}
    try { scopeFilterObj = JSON.parse(form.scope_filter_json || '{}') } catch {
      setMessage('scope_filter is not valid JSON')
      return
    }
    const body: any = {
      user_email: form.user_email.trim().toLowerCase(),
      role: form.role,
      scope_kind: form.scope_kind,
      scope_filter: scopeFilterObj,
      reason: form.reason.trim(),
    }
    if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString()

    setMessage('Saving...')
    try {
      if (creating) {
        const { data } = await api.post('/api/cdm/role-grants', body)
        setMessage(`Created grant ${data.grant?.id?.slice(0,8)} for ${data.grant?.user_email}`)
      } else if (editing) {
        const patch: any = {}
        if (form.expires_at) patch.expires_at = new Date(form.expires_at).toISOString()
        if (form.scope_filter_json) patch.scope_filter = scopeFilterObj
        if (form.reason) patch.reason = form.reason
        await api.patch(`/api/cdm/role-grants/${editing.id}`, patch)
        setMessage(`Updated grant ${editing.id.slice(0,8)}`)
      }
      closeForm()
      load()
    } catch (e: any) {
      setMessage(`Save failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const revoke = async (g: Grant, reason?: string) => {
    if (!confirm(`Revoke ${g.role} grant for ${g.user_email}?`)) return
    try {
      await api.delete(`/api/cdm/role-grants/${g.id}`, { params: reason ? { reason } : {} })
      setMessage(`Revoked ${g.id.slice(0,8)}`)
      load()
    } catch (e: any) {
      setMessage(`Revoke failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const bulkRevoke = async () => {
    if (selected.size === 0) return
    const reason = prompt(`Revoke ${selected.size} grant(s)? Enter a reason for the audit log:`)
    if (reason === null || !reason.trim()) return
    setMessage(`Revoking ${selected.size}...`)
    let ok = 0, fail = 0
    for (const id of selected) {
      try { await api.delete(`/api/cdm/role-grants/${id}`, { params: { reason } }); ok++ } catch { fail++ }
    }
    setMessage(`Revoked ${ok}; ${fail} failed`)
    setSelected(new Set())
    load()
  }

  const decideRequest = async (req: PendingRequest, decision: 'approve' | 'reject') => {
    const notes = prompt(`${decision === 'approve' ? 'Approving' : 'Rejecting'} request from ${req.user_email}. Notes (optional):`) || ''
    try {
      await api.post(`/api/cdm/access-requests/${req.id}/decide`, { decision, notes })
      setMessage(`Request ${decision}d`)
      load()
    } catch (e: any) {
      setMessage(`Decision failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const filteredGrants = useMemo(() => {
    if (!searchEmail) return grants
    const needle = searchEmail.toLowerCase()
    return grants.filter(g => g.user_email.toLowerCase().includes(needle))
  }, [grants, searchEmail])

  const exportCsv = () => {
    const headers = ['user_email','role','scope_kind','scope_filter','granted_by','granted_at','expires_at','days_until_expiry','last_used_at','state','reason']
    const rows = filteredGrants.map(g => [
      g.user_email,
      g.role,
      g.scope_kind,
      JSON.stringify(g.scope_filter || {}),
      g.granted_by,
      g.granted_at || '',
      g.expires_at || '',
      g.days_until_expiry?.toString() || '',
      g.last_used_at || '',
      g.state,
      (g.reason || '').replace(/[\r\n]+/g, ' '),
    ])
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `role-grants-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const toggleSelect = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const toggleSelectAll = () => {
    if (selected.size === filteredGrants.length) setSelected(new Set())
    else setSelected(new Set(filteredGrants.map(g => g.id)))
  }

  const showScopeFilterField =
    form.scope_kind === 'region_view' || form.scope_kind === 'explicit_grant'

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/admin" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Admin
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Lock size={18} className="text-cyan" /> ACCESS GRID
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          every grant · who · what · when · auditor-grade
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={exportCsv} className="btn-ghost" title="Download filtered grid as CSV">
            <Download size={11} /> CSV
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button type="button" onClick={startCreate} className="btn-primary">
            <Plus size={11} /> Grant access
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Tile rollup */}
      {rollup && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Active</div>
            <div className="text-2xl font-mono text-emerald-light">{rollup.active}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Expiring 14d</div>
            <div className={clsx(
              "text-2xl font-mono",
              rollup.expiring_soon > 0 ? "text-amber-light" : "text-text-muted"
            )}>{rollup.expiring_soon}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Suspended</div>
            <div className="text-2xl font-mono text-amber-light">{rollup.suspended}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Revoked</div>
            <div className="text-2xl font-mono text-text-muted">{rollup.revoked}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Global view</div>
            <div className={clsx(
              "text-2xl font-mono",
              rollup.global_view > 0 ? "text-rose-light" : "text-text-muted"
            )}>{rollup.global_view}</div>
          </div>
        </div>
      )}

      {/* Pending requests strip */}
      {pending.length > 0 && (
        <div className="card p-3">
          <div className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-light" /> Pending access requests ({pending.length})
          </div>
          <div className="space-y-1">
            {pending.map(req => (
              <div key={req.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-border last:border-0">
                <span className="font-mono text-text-secondary">{req.user_email}</span>
                <span className="text-text-muted">requesting</span>
                <span className="font-semibold text-blue-light">{req.role}</span>
                <span className="text-text-muted">/ {req.scope_kind}</span>
                <span className="text-text-muted truncate flex-1">— {req.reason}</span>
                <button onClick={() => decideRequest(req, 'approve')} className="btn-ghost text-emerald-light">
                  <CheckCircle size={11} /> Approve
                </button>
                <button onClick={() => decideRequest(req, 'reject')} className="btn-ghost text-rose-light">
                  <X size={11} /> Reject
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          {STATES.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStateFilter(s)}
              className={clsx(
                'px-2 py-1 text-[10px] uppercase tracking-widest rounded border',
                stateFilter === s
                  ? 'bg-blue/20 border-blue text-blue-light'
                  : 'border-border text-text-muted hover:text-text-secondary'
              )}
            >{s}</button>
          ))}
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                className="input text-xs">
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value)}
                className="input text-xs">
          <option value="">All scopes</option>
          {SCOPE_KINDS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs text-text-secondary">
          <input type="checkbox" checked={expiringSoon}
                 onChange={e => setExpiringSoon(e.target.checked)} />
          <Clock size={11} /> Expiring &lt;14d
        </label>
        <input
          type="text" value={searchEmail}
          onChange={e => setSearchEmail(e.target.value)}
          placeholder="search email..."
          className="input text-xs flex-1 min-w-[180px]"
        />
        {selected.size > 0 && (
          <button type="button" onClick={bulkRevoke} className="btn-ghost text-rose-light">
            <Trash2 size={11} /> Revoke {selected.size}
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="w-6 px-2 py-2">
                <input type="checkbox"
                       checked={selected.size > 0 && selected.size === filteredGrants.length}
                       onChange={toggleSelectAll} />
              </th>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Scope</th>
              <th className="text-left px-3 py-2">Granted by</th>
              <th className="text-left px-3 py-2">Granted</th>
              <th className="text-left px-3 py-2">Expires</th>
              <th className="text-left px-3 py-2">Last used</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredGrants.length === 0 && !loading && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-text-muted">
                No grants match these filters. Click 'Grant access' to create one.
              </td></tr>
            )}
            {filteredGrants.map(g => (
              <tr key={g.id} className="hover:bg-surface-raised/40">
                <td className="px-2 py-1.5">
                  <input type="checkbox"
                         checked={selected.has(g.id)}
                         onChange={() => toggleSelect(g.id)} />
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{g.user_email || <span className="text-text-muted italic">(unknown)</span>}</td>
                <td className="px-3 py-1.5">
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded text-[10px] font-mono',
                    g.role === 'admin'    && 'bg-rose/20 text-rose-light',
                    g.role === 'operator' && 'bg-amber/20 text-amber-light',
                    g.role === 'analyst'  && 'bg-blue/20 text-blue-light',
                    g.role === 'viewer'   && 'bg-surface-raised text-text-secondary',
                  )}>{g.role}</span>
                </td>
                <td className="px-3 py-1.5 text-text-secondary">
                  <div>{g.scope_kind}</div>
                  {Object.keys(g.scope_filter || {}).length > 0 && (
                    <div className="text-[10px] text-text-muted font-mono">{JSON.stringify(g.scope_filter)}</div>
                  )}
                </td>
                <td className="px-3 py-1.5 text-text-muted text-[10px] font-mono">{g.granted_by}</td>
                <td className="px-3 py-1.5 text-text-muted">{fmtDate(g.granted_at)}</td>
                <td className={clsx('px-3 py-1.5', expiryClass(g.days_until_expiry))}>
                  {fmtDate(g.expires_at)}
                  {g.days_until_expiry !== null && (
                    <div className="text-[10px]">
                      {g.days_until_expiry < 0
                        ? `${Math.abs(Math.round(g.days_until_expiry))}d overdue`
                        : `${Math.round(g.days_until_expiry)}d left`}
                    </div>
                  )}
                </td>
                <td className={clsx('px-3 py-1.5', lastUsedClass(g.last_used_at))}>
                  {fmtRel(g.last_used_at)}
                </td>
                <td className="px-3 py-1.5">
                  <span className={clsx(
                    'text-[10px] uppercase tracking-widest',
                    g.state === 'active'    && 'text-emerald-light',
                    g.state === 'suspended' && 'text-amber-light',
                    g.state === 'revoked'   && 'text-text-muted',
                  )}>{g.state}</span>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button type="button" onClick={() => startEdit(g)}
                          className="text-blue-light hover:underline mr-2" title="Edit">
                    <Edit2 size={12} />
                  </button>
                  {g.state !== 'revoked' && (
                    <button type="button" onClick={() => revoke(g)}
                            className="text-rose-light hover:text-rose-light/70" title="Revoke">
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit/create modal */}
      {(editing || creating) && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-4 w-full max-w-xl space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-text-primary">
                {creating ? 'Grant access' : `Edit grant ${editing?.id.slice(0,8)}`}
              </h2>
              <button type="button" onClick={closeForm} className="ml-auto text-text-muted hover:text-text-primary">
                <X size={14} />
              </button>
            </div>

            <div className="space-y-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">User email</span>
                <input
                  type="email" value={form.user_email}
                  onChange={e => setForm({ ...form, user_email: e.target.value })}
                  disabled={!!editing}
                  className="input w-full text-xs"
                  placeholder="user@example.com"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-text-muted">Role</span>
                  <select value={form.role}
                          onChange={e => setForm({ ...form, role: e.target.value })}
                          disabled={!!editing}
                          className="input w-full text-xs">
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-text-muted">Scope kind</span>
                  <select value={form.scope_kind}
                          onChange={e => setForm({ ...form, scope_kind: e.target.value })}
                          disabled={!!editing}
                          className="input w-full text-xs">
                    {SCOPE_KINDS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>

              {showScopeFilterField && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-text-muted">
                    Scope filter (JSON, e.g. {`{"region":"EMEA"}`})
                  </span>
                  <textarea
                    value={form.scope_filter_json}
                    onChange={e => setForm({ ...form, scope_filter_json: e.target.value })}
                    rows={3}
                    spellCheck={false}
                    className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                  />
                </label>
              )}

              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">
                  Expires at (leave blank for role-tier default)
                </span>
                <input
                  type="datetime-local" value={form.expires_at}
                  onChange={e => setForm({ ...form, expires_at: e.target.value })}
                  className="input w-full text-xs"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">Reason (audit trail)</span>
                <textarea
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  rows={3}
                  className="w-full text-xs bg-surface-raised p-2 rounded border border-border"
                  placeholder="Why this user needs this access"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button type="button" onClick={submitForm} className="btn-primary">
                {creating ? 'Grant' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-text-muted text-center">
        Grants are always bounded — defaults: admin/global 90d, operator/analyst 180d, viewer-org 365d, viewer-explicit 30d, ring-fenced 14d.
        State='revoked' rows stay for audit history; the nightly job auto-revokes anything past expires_at.
      </p>
    </div>
  )
}
